import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AvatarOrderDataResponse, AvatarOrderRequest, AvatarOrderResponse, Client } from '@api/client';
import { ORDER_STATUS_NUMBERS, WALK_MODE_NUMBERS } from '@avatar/avatar.constants';
import { LoadingService } from '@services/loading.service';
import { ConfigService } from '@services/config.service';
import { firstValueFrom } from 'rxjs';

interface AvatarOrderSummaryItem {
  id: number;
  status: number | null;
  realTime: number | null;
}

interface AvatarOrderSummaryResponse {
  data?: AvatarOrderSummaryItem[] | null;
}

@Injectable({
  providedIn: 'root'
})
export class AvatarOrderService {
  private readonly client: Client = inject(Client);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly loadingService: LoadingService = inject(LoadingService);
  private readonly configService: ConfigService = inject(ConfigService);

  private readonly cachedOrders: Map<number, AvatarOrderDataResponse> = new Map<number, AvatarOrderDataResponse>();

  private get apiBaseUrl(): string {
    return this.configService.urlBackEndApi.replace(/\/+$/, '');
  }

  public async getOrder(orderId: number | string, useCache: boolean = true): Promise<AvatarOrderDataResponse> {
    const normalizedOrderId: number = this.normalizeOrderId(orderId);
    if (useCache) {
      const cached: AvatarOrderDataResponse | undefined = this.cachedOrders.get(normalizedOrderId);
      if (cached) {
        this.cachedOrders.delete(normalizedOrderId);
        return cached;
      }
    }
    const response: AvatarOrderDataResponse = await firstValueFrom(this.client.avatarOrderGet(normalizedOrderId));
    if (useCache) {
      this.cachedOrders.set(normalizedOrderId, response);
    }
    return response;
  }

  public async getOrdersSummary(orderIds: number[]): Promise<AvatarOrderResponse[]> {
    const uniqueIds: number[] = [];
    const seen: Set<number> = new Set<number>();

    for (const id of orderIds) {
      if (typeof id !== 'number' || !Number.isFinite(id)) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      uniqueIds.push(id);
    }

    if (uniqueIds.length === 0) return [];

    const response: AvatarOrderSummaryResponse = await firstValueFrom(
      this.http.post<AvatarOrderSummaryResponse>(`${this.apiBaseUrl}/avatarOrder/summary`, {
        ids: uniqueIds,
      })
    );

    const items: AvatarOrderSummaryItem[] = Array.isArray(response?.data) ? response.data : [];
    const mapped: AvatarOrderResponse[] = [];

    for (const item of items) {
      const id = item?.id;
      if (typeof id !== 'number' || !Number.isFinite(id)) continue;

      const status = (typeof item.status === 'number' && Number.isFinite(item.status)) ? item.status : undefined;
      const realTime = (typeof item.realTime === 'number' && Number.isFinite(item.realTime)) ? item.realTime : undefined;
      mapped.push(AvatarOrderResponse.fromJS({ id, status, realTime }));
    }

    return mapped;
  }

  public async createOrder(request: AvatarOrderRequest): Promise<AvatarOrderDataResponse> {
    return await firstValueFrom(this.client.avatarOrderPost(request));
  }

  public async acceptOrder(orderId: number, longitude: number, latitude: number): Promise<AvatarOrderResponse> {
    return await firstValueFrom(this.client.avatarOrderAccept(orderId, longitude, latitude));
  }

  public async rejectOrder(orderId: number): Promise<AvatarOrderDataResponse> {
    return await firstValueFrom(this.client.avatarOrderReject(orderId));
  }

  public async cancelOrder(orderId: number): Promise<AvatarOrderDataResponse> {
    return await firstValueFrom(this.client.avatarOrderCancel(orderId));
  }

  public isOrderActive(status: number | undefined): boolean {
    return (status == ORDER_STATUS_NUMBERS.CREATED)
      || (status === ORDER_STATUS_NUMBERS.ACCEPTED)
      || (status === ORDER_STATUS_NUMBERS.PAID);
  }

  public isOrderFinal(status: number | undefined): boolean {
    return (status === ORDER_STATUS_NUMBERS.REJECTED)
      || (status === ORDER_STATUS_NUMBERS.PAYMENT_FAILED)
      || (status === ORDER_STATUS_NUMBERS.COMPLETED)
      || (status === ORDER_STATUS_NUMBERS.PAYMENT_CANCELED)
      || (status === ORDER_STATUS_NUMBERS.CANCELED);
  }

  public hasRequiredFields(order: AvatarOrderResponse | null | undefined): boolean {
    const fromId: number | undefined = order?.from?.id;
    if (!fromId) {
      console.error('Order from id is not defined!');
      return false;
    }
    const isFreeOrder: boolean = (order?.type === WALK_MODE_NUMBERS.FREE);
    const isAudioCall: boolean = (order?.type === WALK_MODE_NUMBERS.AUDIO_CALL);
    return Boolean(order?.rate) || isFreeOrder || isAudioCall;
  }

  public isValidOrder(order: AvatarOrderResponse | null | undefined): boolean {
    return this.isOrderActive(order?.status) && this.hasRequiredFields(order);
  }

  public async checkIncompleteOrderStatus(
    orderId: number,
    onActive: () => Promise<void>,
    onFinal: () => Promise<void>,
  ): Promise<void> {
    try {
      await this.loadingService.start();
      const response: AvatarOrderDataResponse = await this.getOrder(orderId, false);
      const orderStatus: number | undefined = response?.data?.status;

      if (this.isOrderActive(orderStatus)) {
        await onActive();
      } else if (this.isOrderFinal(orderStatus)) {
        console.warn('Order status: ', orderStatus);
        await onFinal();
      }
    } catch (error) {
      console.error(error);
    } finally {
      await this.loadingService.stop();
    }
  }

  private normalizeOrderId(orderId: number | string): number {
    if (typeof orderId === 'number') {
      return orderId;
    }
    const parsedOrderId: number = Number(orderId);
    if (Number.isNaN(parsedOrderId)) {
      throw new Error('Invalid order id');
    }
    return parsedOrderId;
  }
}
